import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { setOnboardingState, setCurrentXp, setSubscriptionStatus, setWordCounts, setLoginDay, setHintProgress } from "./state";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import api from "./api";
import { useTheme } from "next-themes";
import LoadingPage from "./pages/loadingPage";
import WelcomePage from "./pages/welcomePage/index3.0.jsx";
import MetaPixel from "./components/MetaPixel";
import { useFeatureUnlockToasts } from "./hooks/useFeatureUnlockToast";
import { useTaskUnlockToast } from "./hooks/useTaskUnlockToast";
import { TourController } from "./components/OnboardingPopover/controller";
import { HintNudgeController } from "./components/OnboardingPopover/nudgeController";
import { FeatureLockGuard } from "./hooks/useFeatureLock";
import { ReminderController } from "./components/OnboardingPopover/reminderController";

const OnboardingPage = lazy(() => import("./pages/onboardingPage"));
const SignupPage = lazy(() => import("./pages/signupPage"));
const LoginPage = lazy(() => import("./pages/loginPage"));
const PreOnboardingPage = lazy(() => import("./pages/preOnboardingPage/index.jsx"));
const DashboardPage = lazy(() => import("./pages/dashboardPage"));
const GrammarPage = lazy(() => import("./pages/grammarPage"));
const VocabularyPage = lazy(() => import("./pages/vocabularyPage"));
const GeneratedReadingPage = lazy(() => import("./pages/generatedReadingPage/index.2.0.jsx"));
const MaterialsPage = lazy(() => import("./pages/materialsPage/index.jsx"));
const ReadingPage = lazy(() => import("./pages/readingPage/index.jsx"));
const VideoPage = lazy(() => import("./pages/videoPage/index.jsx"));
const ImportedMaterialPage = lazy(() => import("./pages/importedMaterialPage"));
const WritingPracticePage = lazy(() => import("./pages/writingPracticePage"));
const PaymentStatusPage = lazy(() => import("./pages/paymentStatusPage"));
const SubscriptionRenewPage = lazy(() => import("./pages/subscriptionRenewPage"));
const CustomerPortalPage = lazy(() => import("./pages/customerPortalPage"))

const VerifyResetPasswordTokenPage = lazy(() => import("./pages/verifyResetPasswordTokenPage"));
const ResetPasswordPage = lazy(() => import("./pages/resetPasswordPage"));

const ReportCompromisePage = lazy(() => import("./pages/reportCompromisePage"));
const ResetPasswordAftrerCompromisePage = lazy(() => import("./pages/resetPasswordAftrerCompromisePage"));

const AccountDeletedPage = lazy(() => import("./pages/accountDeletedPage"));

const NotFoundPage = lazy(() => import("./pages/notFoundPage"));

const AdminDashboard = lazy(() => import("./pages/adminDashboard"))

function App() {
  const dispatch = useDispatch();

  const mode = useSelector((state) => state.mode);
  const user = useSelector((state) => state.user)

  const expiresAt = useSelector((state) => state.expiresAt);
  const token = useSelector((state) => state.token);

  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isAuth, setIsAuth] = useState(() => {
    return token && new Date(expiresAt) > new Date();
  });

  const subscriptionStatus = useSelector((state) => state.subscriptionStatus);
  const onboardingStep = useSelector((state) => state.onboardingStep);
  const [subscriptionPaid, setSubscriptionPaid] = useState(subscriptionStatus === "active" || subscriptionStatus === "trialing");



  const triggerRefresh = async () => {
    try {
      await api.post("/auth/trigger-refresh", {
        userId: user?.id
      })
    } catch (error) {

    }
  }

  const { theme, setTheme } = useTheme()
  useEffect(() => {
    if (!isAuth || !subscriptionPaid) {
      setTheme("light")
      return;
    }
    // updateStreak();
  }, [isAuth, subscriptionPaid]);

  useEffect(() => {
    // setIsAuth(token && new Date(expiresAt) > new Date())
    setIsAuth(false)
  }, [token, expiresAt]);

  useEffect(() => {
    setSubscriptionPaid(subscriptionStatus === "active" || subscriptionStatus === "trialing")
  }, [subscriptionStatus])

  useEffect(() => {
    if (!expiresAt) {
      setIsAuthLoading(false);
      return;
    }

    if (new Date(expiresAt) <= new Date()) {
      setIsAuthLoading(true);
      triggerRefresh().catch(() => { }).finally(() => setIsAuthLoading(false));
    }
  }, []);

  useEffect(() => {
    if (isAuth) {
      document.body.style.background = "#D7E5ED";
      api.get('/subscription/status')
        .then(res => {
          dispatch(setSubscriptionStatus({ subscriptionStatus: res.data.status || "trialing" }));
          dispatch(setCurrentXp({ currentXp: res.data.currentXp }));
          dispatch(setOnboardingState({
            onboardingStep:
              res.data.onboardingStep || "pre-onboarding"
          }));
          dispatch(setWordCounts(res.data.wordCounts));
          dispatch(setHintProgress(res.data.hintProgress));
          dispatch(setLoginDay(res.data.dailyTasksAmount))
        })
        .catch(err => {

        })
    } else document.body.style.background = "#5995B8";
  }, [isAuth]);


  const landingPageRedirect = !isAuth ?
    (isAuthLoading ? <LoadingPage /> : <WelcomePage />)
    :
    subscriptionPaid ? <Navigate to="/dashboard" /> : <Navigate to="/subscription-renew" />

  const signupLoginGuard = (page) => {
    return  !isAuth ?  (isAuthLoading ? <LoadingPage /> : page) : subscriptionPaid ? <Navigate to="/dashboard" /> : <Navigate to="/subscription-renew" />
  }

  const preOnboardingGuard = (page) => {
    if (!isAuth || onboardingStep !== "pre-onboarding") return <Navigate to="/" />
    if (!subscriptionPaid) return <Navigate to="/subscription-renew" />
    return page
  }
  const authRedirect = (page) => {
    if (!isAuth) return <Navigate to="/" />
    if (!subscriptionPaid) return <Navigate to="/subscription-renew" />
    if (onboardingStep === "pre-onboarding") return <Navigate to="/pre-onboarding" />
    return page
  }

  useFeatureUnlockToasts();
  useTaskUnlockToast()

  return (
    <div className={`${isAuth ? mode : "light"}`} >
      <BrowserRouter>
        <MetaPixel />
        {(isAuth && subscriptionPaid) &&
          <>
            <TourController />
            <HintNudgeController />
            <ReminderController />
          </>
        }

        <Suspense fallback={<LoadingPage />}>
          <Routes>

            <Route path="/admin-nastya" element={user?.isAdmin ? <AdminDashboard /> : <Navigate to="/" />} />

            <Route path="/" element={landingPageRedirect} />
            <Route path="/signup" element={signupLoginGuard(<SignupPage/>)} />
            <Route path="/login" element={signupLoginGuard(<LoginPage/>)} />
            <Route path="/faq" element={landingPageRedirect} />
            <Route path="/privacy-policy" element={landingPageRedirect} />
            <Route path="/terms-of-service" element={landingPageRedirect} />
            <Route path="/commerce-disclosure" element={landingPageRedirect} />
            <Route path="/about-us" element={landingPageRedirect} />


            <Route path="/verify-reset-password" element={<VerifyResetPasswordTokenPage />} />
            <Route path="/reset-new-password" element={!isAuth ? <ResetPasswordPage /> : subscriptionPaid ? <Navigate to="/dashboard" /> : <Navigate to="/subscription-renew" />} />
            <Route path="/report-compromise" element={<ReportCompromisePage />} />
            <Route path="/security-password-reset" element={<ResetPasswordAftrerCompromisePage />} />

            <Route
              path="/subscription-renew"
              element={
                isAuth && (subscriptionStatus === "past_due" || subscriptionStatus === "paused") ?
                  <CustomerPortalPage />
                  :
                  (isAuth && !subscriptionPaid) ?
                    <SubscriptionRenewPage isFirstTime={subscriptionStatus !== "canceled"} /> :
                    <Navigate to="/" />
              }
            />

            <Route
              path="/checkout"
              element={
                isAuth && (subscriptionStatus === "past_due" || subscriptionStatus === "paused") ?
                  <CustomerPortalPage />
                  :
                  (isAuth && ["trialing", "inactive"].includes(subscriptionStatus)) ?
                    <SubscriptionRenewPage /> :
                    <Navigate to="/" />
              }
            />

            <Route path="/pre-onboarding" element={preOnboardingGuard(<PreOnboardingPage />)} />
            <Route path="/dashboard" element={authRedirect(<DashboardPage />)} />
            <Route path="/grammar" element={authRedirect(<GrammarPage />)} />
            <Route path="/vocabulary" element={authRedirect(<VocabularyPage />)} />
            {/* <Route path="/generated-reading" element={authRedirect(<GeneratedReadingPage />)} /> */}
            <Route path="/materials" element={authRedirect(
              <FeatureLockGuard feature="materials">
                <MaterialsPage />
              </FeatureLockGuard>
            )} />
            <Route path="/reading/:id" element={authRedirect(
              <FeatureLockGuard feature="materials">
                <ReadingPage />
              </FeatureLockGuard>
            )} />
            <Route path="/video/:id" element={authRedirect(
              <FeatureLockGuard feature="materials">
                <VideoPage />
              </FeatureLockGuard>
            )} />
            {/* <Route path="/imported-material" element={authRedirect(<ImportedMaterialPage />)} /> */}
            <Route path="/writing-practice" element={authRedirect(
              <FeatureLockGuard feature="chat">
                <WritingPracticePage />
              </FeatureLockGuard>
            )} />


            <Route path="/payment-status" element={<PaymentStatusPage />} />
            <Route path="/account-deleted" element={<AccountDeletedPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>

        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
